import { useState } from 'react';
import { Button, Input, Modal, NativeSelect } from '@immo/ui-kit';
import { FleetWorkerSchema, type FleetCommandView } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { FollowedCommand, FollowedCommandFooter } from './followed-command';

type Props = {
  readonly machineName: string;
  /** The projects the machine's workers serve; none chosen unlocks the stack in each of them. */
  readonly projects: ReadonlyArray<string>;
  /** The issued command, as the page's poll last read it; `null` while the form is up. */
  readonly command: FleetCommandView | null;
  readonly busy: boolean;
  /** Why the backend refused the issue, translated. */
  readonly error: string | null;
  /** `undefined` for either: every project, and the host's manual stack. */
  readonly onSubmit: (project: string | undefined, worker: string | undefined) => void;
  readonly onAgain: () => void;
  readonly onClose: () => void;
};

/**
 * `e2e-unlock` (IMM-1153): break the lock on one e2e stack of the host, for an
 * operator who cannot reach it. The form names whose stack — a worker, or none
 * for the host's manual stack — checked against `FleetWorkerSchema`, the check
 * the backend makes again. Once issued, the dialog follows the command until it
 * settles with who held the lock until then.
 */
export function E2eUnlockDialog({ machineName, projects, command, busy, error, onSubmit, onAgain, onClose }: Props) {
  const t = useTranslations('pages.admin.fleet.e2e-unlock');
  const tFleet = useTranslations('pages.admin.fleet');
  const [project, setProject] = useState('');
  const [worker, setWorker] = useState('');

  if (command !== null) {
    return (
      <Modal
        open
        onClose={onClose}
        confirmOnClose={false}
        size="lg"
        title={t('title')}
        footer={
          <FollowedCommandFooter command={command} againLabel={t('again-cta')} onAgain={onAgain} onClose={onClose} />
        }
      >
        <FollowedCommand
          scope={t('scope-line', {
            machine: machineName,
            project: command.project ?? t('every-project'),
            worker: command.worker ?? tFleet('activity.e2e-unlock-manual'),
          })}
          command={command}
        >
          <E2eUnlockResult command={command} />
        </FollowedCommand>
      </Modal>
    );
  }

  const trimmed = worker.trim();
  const workerInvalid = trimmed !== '' && !FleetWorkerSchema.safeParse(trimmed).success;
  const submit = () => {
    if (!workerInvalid) onSubmit(project === '' ? undefined : project, trimmed === '' ? undefined : trimmed);
  };

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      confirmOnClose={false}
      size="md"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            {tFleet('cancel-cta')}
          </Button>
          <Button type="button" onClick={submit} disabled={busy || workerInvalid}>
            {t('cta')}
          </Button>
        </>
      }
    >
      <p className="font-mono text-base-sm text-text-secondary">{machineName}</p>
      <p className="mt-3 text-sm text-text-secondary">{t('body')}</p>

      <form
        className="mt-4 flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {projects.length > 1 && (
          <NativeSelect
            value={project}
            onChange={setProject}
            disabled={busy}
            aria-label={t('project-field')}
            options={[
              { value: '', label: t('every-project') },
              ...projects.map((name) => ({ value: name, label: name })),
            ]}
          />
        )}
        <Input
          className="font-mono text-base-sm"
          autoFocus
          value={worker}
          onChange={(event) => setWorker(event.target.value)}
          disabled={busy}
          invalid={workerInvalid}
          aria-label={t('worker-field')}
          spellCheck={false}
          autoComplete="off"
        />
        {workerInvalid ? (
          <p role="alert" className="text-sm text-danger-600">
            {t('worker-invalid')}
          </p>
        ) : (
          <p className="text-sm text-text-tertiary">{t('worker-hint')}</p>
        )}
      </form>

      {error !== null && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {error}
        </p>
      )}
    </Modal>
  );
}

/** Who held the lock until the unlock broke it — or that nobody did. */
function E2eUnlockResult({ command }: { readonly command: FleetCommandView }) {
  const t = useTranslations('pages.admin.fleet.e2e-unlock');
  const payload = command.payload?.kind === 'e2e-unlock' ? command.payload : null;

  if (payload === null) return null;

  if (payload.holder === null) return <p className="text-sm text-text-secondary">{t('no-holder')}</p>;

  return (
    <div className="flex flex-col gap-1 font-mono text-base-sm text-text-primary">
      <p className="break-words">
        {t('holder', { owner: payload.holder.owner, host: payload.holder.host, since: payload.holder.since })}
      </p>
      <p className="break-words text-text-secondary">{t('holder-worktree', { worktree: payload.holder.worktree })}</p>
    </div>
  );
}
